import styles from "./LoadingSkeleton.module.scss";

type SkeletonType =
  | "default"
  | "text"
  | "card"
  | "tableRow"
  | "image"
  | "button"
  | "page"
  | "dashboard"
  | "orders"
  | "listings"
  | "cart";

type Props = {
  type?: SkeletonType;
  count?: number;
};

/**
 * Reusable loading skeleton component. Page-level types
 * (page/dashboard/orders/listings/cart) render their own structure;
 * the leaf types (default/text/card/tableRow/image/button) repeat
 * `count` times.
 */
const LoadingSkeleton = ({ type = "default", count = 1 }: Props) => {
  // Page-level skeletons render their own structure
  if (type === "page") {
    return (
      <div className={styles.pageContainer} aria-live="polite" aria-busy="true">
        <div className={styles.pageHeader}>
          <div className={`${styles.skeleton} ${styles.titleSkeleton}`}>
            <div className={styles.skeletonShimmer} />
          </div>
        </div>
        <div className={styles.pageContent}>
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.contentBlock}`}>
              <div className={styles.skeletonShimmer} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "dashboard") {
    return (
      <div className={styles.dashboardContainer} aria-live="polite" aria-busy="true">
        <div className={styles.dashboardHeader}>
          <div className={`${styles.skeleton} ${styles.tabsSkeleton}`}>
            <div className={styles.skeletonShimmer} />
          </div>
          <div className={`${styles.skeleton} ${styles.datePickerSkeleton}`}>
            <div className={styles.skeletonShimmer} />
          </div>
        </div>
        <div className={styles.metricsGrid}>
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.metricCard}`}>
              <div className={styles.skeletonShimmer} />
            </div>
          ))}
        </div>
        <div className={styles.chartsGrid}>
          <div className={`${styles.skeleton} ${styles.chartSkeleton}`}>
            <div className={styles.skeletonShimmer} />
          </div>
          <div className={`${styles.skeleton} ${styles.chartSkeleton}`}>
            <div className={styles.skeletonShimmer} />
          </div>
        </div>
      </div>
    );
  }

  if (type === "orders") {
    return (
      <div className={styles.ordersContainer} aria-live="polite" aria-busy="true">
        <div className={styles.ordersHeader}>
          <div className={`${styles.skeleton} ${styles.tabsSkeleton}`}>
            <div className={styles.skeletonShimmer} />
          </div>
          <div className={`${styles.skeleton} ${styles.searchSkeleton}`}>
            <div className={styles.skeletonShimmer} />
          </div>
        </div>
        <div className={styles.tableContainer}>
          <div className={`${styles.skeleton} ${styles.tableHeader}`}>
            <div className={styles.skeletonShimmer} />
          </div>
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.tableRow}`}>
              <div className={styles.skeletonShimmer} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "listings") {
    return (
      <div className={styles.listingsContainer} aria-live="polite" aria-busy="true">
        <div className={styles.listingsHeader}>
          <div className={`${styles.skeleton} ${styles.titleSkeleton}`}>
            <div className={styles.skeletonShimmer} />
          </div>
          <div className={`${styles.skeleton} ${styles.buttonSkeleton}`}>
            <div className={styles.skeletonShimmer} />
          </div>
        </div>
        <div className={styles.listingsGrid}>
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.listingCard}`}>
              <div className={styles.skeletonShimmer} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "cart") {
    return (
      <div className={styles.cartContainer} aria-live="polite" aria-busy="true">
        {Array.from({ length: count }, (_, vendorIndex) => (
          <div key={vendorIndex} className={styles.cartVendorSection}>
            <div className={styles.cartVendorHeader}>
              <div className={`${styles.skeleton} ${styles.cartVendorImage}`}>
                <div className={styles.skeletonShimmer} />
              </div>
              <div className={styles.cartVendorInfo}>
                <div className={`${styles.skeleton} ${styles.cartVendorName}`}>
                  <div className={styles.skeletonShimmer} />
                </div>
                <div className={`${styles.skeleton} ${styles.cartVendorCount}`}>
                  <div className={styles.skeletonShimmer} />
                </div>
              </div>
              <div className={`${styles.skeleton} ${styles.cartVendorButton}`}>
                <div className={styles.skeletonShimmer} />
              </div>
            </div>
            <div className={styles.cartListingsList}>
              {Array.from({ length: 2 }, (_, listingIndex) => (
                <div key={listingIndex} className={styles.cartListingRow}>
                  <div className={`${styles.skeleton} ${styles.cartListingImage}`}>
                    <div className={styles.skeletonShimmer} />
                  </div>
                  <div className={styles.cartListingInfo}>
                    <div className={`${styles.skeleton} ${styles.cartListingTitle}`}>
                      <div className={styles.skeletonShimmer} />
                    </div>
                    <div className={`${styles.skeleton} ${styles.cartListingPrice}`}>
                      <div className={styles.skeletonShimmer} />
                    </div>
                  </div>
                  <div className={styles.cartListingQuantity}>
                    <div className={`${styles.skeleton} ${styles.cartQuantityBtn}`}>
                      <div className={styles.skeletonShimmer} />
                    </div>
                    <div className={`${styles.skeleton} ${styles.cartQuantityValue}`}>
                      <div className={styles.skeletonShimmer} />
                    </div>
                    <div className={`${styles.skeleton} ${styles.cartQuantityBtn}`}>
                      <div className={styles.skeletonShimmer} />
                    </div>
                  </div>
                  <div className={`${styles.skeleton} ${styles.cartRemoveBtn}`}>
                    <div className={styles.skeletonShimmer} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Default skeleton items
  const skeletons = Array.from({ length: count }, (_, i) => (
    <div key={i} className={`${styles.skeleton} ${styles[type]}`} aria-hidden="true">
      <div className={styles.skeletonShimmer} />
    </div>
  ));

  return (
    <div className={styles.skeletonContainer} aria-live="polite" aria-busy="true">
      {skeletons}
    </div>
  );
};

export default LoadingSkeleton;
